import { useCallback, useEffect, useRef, useState } from 'react'
import catRunningBgm from './game/bgm'

export default function useCatRunningBgm({ shouldPlay, pauseInsteadOfStop = false, worldRef }) {
  const [muted, setMuted] = useState(() => catRunningBgm.loadMutedPreference())
  const lastPlayingStageKeyRef = useRef(null)
  const switchAttemptedRef = useRef(null)
  const shouldPlayRef = useRef(shouldPlay)
  const mutedRef = useRef(muted)

  useEffect(() => {
    shouldPlayRef.current = shouldPlay
  }, [shouldPlay])

  useEffect(() => {
    mutedRef.current = muted
  }, [muted])

  useEffect(() => {
    catRunningBgm.preload()
  }, [])

  useEffect(() => {
    if (!shouldPlay || !worldRef) {
      // 1차 사망: pause만 해서 이어하기 후 같은 트랙을 재개할 수 있게 한다.
      // stop은 unlock까지 지워서 광고 복귀 후 재생이 막힌다.
      if (pauseInsteadOfStop) {
        catRunningBgm.pause()
      } else {
        catRunningBgm.stop()
        lastPlayingStageKeyRef.current = null
        switchAttemptedRef.current = null
      }
      return undefined
    }

    let frameId = 0
    let isActive = true

    const syncStageBgm = () => {
      if (!isActive) return

      const stageKey = worldRef.current?.stageKey
      if (!stageKey) {
        frameId = window.requestAnimationFrame(syncStageBgm)
        return
      }

      const isPlaying = catRunningBgm.isPlaying()
      const activeKey = catRunningBgm.getActiveStageKey()
      const holdingSilent = catRunningBgm.isHoldingSilent()

      // 올바른 스테이지가 재생 중
      if (isPlaying && activeKey === stageKey) {
        if (holdingSilent) {
          // 무음 프라임 → 실제 스테이지와 동일하면 이때 처음 들리게 (1회만)
          const revealKey = `reveal:${stageKey}`
          if (switchAttemptedRef.current !== revealKey) {
            switchAttemptedRef.current = revealKey
            catRunningBgm.playStage(stageKey).then((didPlay) => {
              if (!isActive) return
              if (didPlay) {
                lastPlayingStageKeyRef.current = stageKey
                switchAttemptedRef.current = stageKey
              } else {
                switchAttemptedRef.current = null
              }
            })
          }
        } else {
          catRunningBgm.ensureAudible()
          lastPlayingStageKeyRef.current = stageKey
          switchAttemptedRef.current = stageKey
        }
        frameId = window.requestAnimationFrame(syncStageBgm)
        return
      }

      // 시작 무음(또는 다른 트랙) → 랜덤 스테이지로 crossfade
      if (isPlaying && activeKey && activeKey !== stageKey) {
        if (catRunningBgm.isUnlocked() && switchAttemptedRef.current !== stageKey) {
          switchAttemptedRef.current = stageKey
          catRunningBgm.playStage(stageKey).then((didPlay) => {
            if (!isActive) return
            if (didPlay) {
              lastPlayingStageKeyRef.current = stageKey
            } else {
              switchAttemptedRef.current = null
              // 무음 프라임 중이면 잘못된 forest를 키지 않는다
              if (!catRunningBgm.isHoldingSilent()) {
                catRunningBgm.ensureAudible()
              }
            }
          })
        }

        frameId = window.requestAnimationFrame(syncStageBgm)
        return
      }

      const shouldResume = !isPlaying && catRunningBgm.isUnlocked() && !mutedRef.current
      if (shouldResume) {
        catRunningBgm.playStage(stageKey).then((didPlay) => {
          if (!isActive) return
          if (didPlay) {
            lastPlayingStageKeyRef.current = stageKey
          }
        })
      }

      frameId = window.requestAnimationFrame(syncStageBgm)
    }

    syncStageBgm()

    return () => {
      isActive = false
      window.cancelAnimationFrame(frameId)
    }
  }, [pauseInsteadOfStop, shouldPlay, worldRef])

  useEffect(() => {
    if (!shouldPlay || muted) return undefined

    const unlock = () => {
      if (!shouldPlayRef.current || mutedRef.current) return
      const stageKey = worldRef.current?.stageKey
      catRunningBgm.unlockFromUserGesture(stageKey).then((didPlay) => {
        if (didPlay) {
          lastPlayingStageKeyRef.current = stageKey ?? catRunningBgm.getActiveStageKey()
          switchAttemptedRef.current = lastPlayingStageKeyRef.current
        }
      })
    }

    document.addEventListener('pointerdown', unlock, { capture: true })
    document.addEventListener('touchstart', unlock, { capture: true })

    return () => {
      document.removeEventListener('pointerdown', unlock, { capture: true })
      document.removeEventListener('touchstart', unlock, { capture: true })
    }
  }, [muted, shouldPlay, worldRef])

  const unlockFromUserGesture = useCallback(() => {
    if (muted) return

    const stageKey = worldRef.current?.stageKey

    if (
      catRunningBgm.isPlaying() &&
      !catRunningBgm.isHoldingSilent() &&
      (!stageKey || catRunningBgm.getActiveStageKey() === stageKey)
    ) {
      catRunningBgm.ensureAudible()
      lastPlayingStageKeyRef.current = stageKey ?? catRunningBgm.getActiveStageKey()
      return
    }

    catRunningBgm.unlockFromUserGesture(stageKey).then((didPlay) => {
      if (didPlay) {
        lastPlayingStageKeyRef.current = stageKey ?? catRunningBgm.getActiveStageKey()
        switchAttemptedRef.current = lastPlayingStageKeyRef.current
      } else if (!catRunningBgm.isHoldingSilent()) {
        catRunningBgm.ensureAudible()
      }
    })
  }, [muted, worldRef])

  const stopBgm = useCallback(() => {
    catRunningBgm.stop()
    lastPlayingStageKeyRef.current = null
    switchAttemptedRef.current = null
  }, [])

  const toggleMuted = useCallback(() => {
    setMuted((current) => {
      const next = !current
      catRunningBgm.setMuted(next).then((didPlay) => {
        if (!next && didPlay) {
          lastPlayingStageKeyRef.current = worldRef.current?.stageKey ?? catRunningBgm.getActiveStageKey()
          switchAttemptedRef.current = lastPlayingStageKeyRef.current
        }
      })
      return next
    })
  }, [worldRef])

  return {
    muted,
    toggleMuted,
    unlockFromUserGesture,
    stopBgm,
  }
}
